import { findModeByExtension, findModeByFileName, findModeByMIME } from 'codemirror';
import * as _ from 'lodash';

import { useRevisionFileBlob } from '../../../../../hooks/api';

type CodeMirrorModeInfo = ReturnType<typeof findModeByFileName>;

//  Determines the "mode" for CodeMirror to display a given file.
//  It can infer this from the `Content-type` header, or from the file extension.
const getCodeMirrorModeFromContentType = (filePath: string, contentType?: string | null): CodeMirrorModeInfo => {
  const fileExtension = _.last(filePath.split('.')) || 'txt';
  return findModeByMIME(contentType || '') ||
    findModeByExtension(fileExtension) ||
    findModeByFileName(filePath);
};

/**
 * Hook which will fetch the contents of a revision file match.
 *
 * This will return the CodeMirror file "mode" (which informs it
 * about the type of file it is viewing) as well as the raw
 * contents of the file as a UTF-8 string.
 *
 * The file contents are re-fetched if the file path or revision locator change.
 */
function useRevisionFileBlobForCodemirror(revisionLocator: string, filePath: string) {
  const { data, isFetching, isError, error } = useRevisionFileBlob(revisionLocator, filePath);
  const [contentType, fileContents] = data || [];
  //  Derive the mode from the content type, if it has been fetched.
  const mode = fileContents ? getCodeMirrorModeFromContentType(filePath, contentType) : undefined;
  //  If the file is not found, we display a "reanalyze dependency" button.
  const isNotFound = isError && (error as Error)?.message?.includes('Not Found');

  return {
    mode,
    error,
    isError,
    //  Note that this is slightly different from isLoading from React Query.
    //  isLoading is basically `isFetching || !data`, but we need to stop rendering
    //  the CodeMirror component when re-fetching (so it can highlight stuff),
    //  so instead we use isFetching here.
    isLoading: isFetching,
    isNotFound,
    fileContents,
  };
}

export default useRevisionFileBlobForCodemirror;
